import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  MessageSquare,
  Settings,
  Building2,
  X,
  Smartphone,
  MessageCircle,
  ChevronDown,
  ShoppingBag,
  HeadphonesIcon,
} from 'lucide-react';

import { ROUTES } from '../../../utils/constants';
import useAuth from '../../../hooks/useAuth';
import useBot from '../../../hooks/useBot';

const SUPPORT_WHATSAPP = 'https://wa.me/5598981420180';

const BotSelector = () => {
  const { accessibleBots, selectedBotId, selectBot, hasMultipleBots } = useBot();
  if (!hasMultipleBots) return null;

  const selectedBot = accessibleBots.find((b) => b.id === selectedBotId);
  const getBotLabel = (type) => (type === 'sales' ? 'Bot de Vendas' : 'Bot de SAC');

  return (
    <div className="px-3 pt-2 pb-3 border-b border-slate-100 dark:border-slate-800">
      <p className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2 px-1">
        Bot Ativo
      </p>
      <div className="relative">
        <select
          value={selectedBotId || ''}
          onChange={(e) => selectBot(e.target.value)}
          className="
            w-full appearance-none pl-3 pr-8 py-2.5
            bg-slate-50 dark:bg-slate-800
            border border-slate-200 dark:border-slate-700
            rounded-lg text-sm font-medium
            text-slate-700 dark:text-slate-200
            focus:outline-none focus:ring-2 focus:ring-primary-400/50
            cursor-pointer transition-colors
          "
        >
          {accessibleBots.map((bot) => (
            <option key={bot.id} value={bot.id}>
              {bot.name}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
      </div>
      {selectedBot && (
        <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-1.5 px-1">
          {getBotLabel(selectedBot.type)}
        </p>
      )}
    </div>
  );
};

const Sidebar = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.type === 'admin';

  const menuItems = [
    { title: 'Visão Geral',  icon: LayoutDashboard, path: ROUTES.OVERVIEW },
    { title: 'Conversas',    icon: MessageSquare,   path: ROUTES.CONVERSATIONS },
  ];

  if (isAdmin) {
    menuItems.push(
      { title: 'Tenants',              icon: Building2,   path: ROUTES.TENANTS },
      { title: 'Instâncias Evolution', icon: Smartphone,  path: '/dashboard/evolution' }
    );
  }

  menuItems.push({ title: 'Configurações', icon: Settings, path: ROUTES.SETTINGS });

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`
          fixed top-0 left-0 z-50 h-screen w-64
          bg-white dark:bg-slate-900
          border-r border-slate-200 dark:border-slate-800
          transform transition-transform duration-300 ease-in-out
          lg:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          flex flex-col
        `}
      >
        {/* Logo — mobile only */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-slate-100 dark:border-slate-800 lg:hidden">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Mara Labs" className="h-7 w-auto" />
            <span className="text-lg font-bold gradient-text font-display">Mara Labs</span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Bot Selector */}
        {!isAdmin && <BotSelector />}

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto custom-scrollbar">
          <p className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-3 px-2">
            Menu
          </p>
          <div className="space-y-0.5">
            {menuItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => { if (window.innerWidth < 1024) onClose(); }}
                className={({ isActive }) => `
                  flex items-center gap-3 px-3 py-2.5 rounded-lg
                  transition-all duration-150 group
                  ${isActive
                    ? 'bg-primary-500 text-white shadow-sm shadow-primary-500/25'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-200'
                  }
                `}
              >
                {({ isActive }) => (
                  <>
                    <item.icon className={`w-4.5 h-4.5 flex-shrink-0 ${isActive ? 'text-white' : 'text-slate-400 dark:text-slate-500 group-hover:text-slate-600 dark:group-hover:text-slate-300'}`} style={{ width: '18px', height: '18px' }} />
                    <span className="text-sm font-medium">{item.title}</span>
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </nav>

        {/* Support */}
        <div className="px-4 py-4 border-t border-slate-100 dark:border-slate-800">
          <a
            href={SUPPORT_WHATSAPP}
            target="_blank"
            rel="noopener noreferrer"
            className="
              flex items-center gap-2.5 px-3 py-2.5 rounded-lg w-full
              text-slate-500 dark:text-slate-400
              hover:bg-slate-100 dark:hover:bg-slate-800
              hover:text-primary-600 dark:hover:text-primary-400
              transition-colors group
            "
          >
            <div className="w-7 h-7 bg-primary-50 dark:bg-primary-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
              <MessageCircle className="w-3.5 h-3.5 text-primary-500" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">Suporte</p>
              <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate">Falar com a equipe</p>
            </div>
          </a>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
